import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Fallback used before the backend responds (or if it fails).
const FALLBACK = {
  whatsapp_number: '+5547999201212',
  gtm_id: 'GTM-TQ9BW6KM',
  ga_id:  'G-Z52WGQGCRB',
};

const SettingsContext = createContext({ settings: FALLBACK, loading: true });

// Guarantees at most one GTM `<script>` is injected per page load — no
// matter how many times SettingsProvider re-renders or refetches.
let _gtmInjectedFor = null;

const injectGtm = (gtmId) => {
  if (!gtmId || typeof document === 'undefined') return;
  if (_gtmInjectedFor === gtmId) return;
  if (document.querySelector('script[data-gtm-loader]')) {
    _gtmInjectedFor = gtmId;
    return;
  }
  window.dataLayer = window.dataLayer || [];
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  s.setAttribute('data-gtm-loader', gtmId);
  document.head.appendChild(s);
  _gtmInjectedFor = gtmId;
};

// Digits-only helper so links like `wa.me/...` never break because of
// masked input (`+55 (47) 99920-1212` → `5547999201212`).
export const normalizeWhatsapp = (raw) => {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  // If the operator forgot the +55 prefix, add it. Numbers <= 11 digits
  // are treated as Brazilian mobile/landline; longer strings are assumed
  // to already include the country code.
  return digits.length <= 11 ? '55' + digits : digits;
};

export const buildWaLink = (raw, { text } = {}) => {
  const n = normalizeWhatsapp(raw);
  if (!n) return '#';
  const q = text ? `?text=${encodeURIComponent(text)}` : '';
  return `https://wa.me/${n}${q}`;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    // Inject GTM immediately with the fallback so we start capturing
    // History Change events even before the backend responds.
    injectGtm(FALLBACK.gtm_id);
    axios
      .get(`${API_URL}/public/settings`)
      .then((res) => {
        if (mounted && res.data && typeof res.data === 'object') {
          const merged = { ...FALLBACK, ...res.data };
          setSettings(merged);
          // If the operator changed the container ID in the admin,
          // the browser has already loaded gtm.js for the fallback ID.
          // Injecting a second loader would double-fire tags, so we
          // only swap when the page reloads. Log for observability.
          if (merged.gtm_id && merged.gtm_id !== _gtmInjectedFor) {
            // eslint-disable-next-line no-console
            console.info(
              `[settings] GTM ID changed to ${merged.gtm_id} — refresh to apply.`
            );
          }
        }
      })
      .catch(() => {
        // Silently fall back to defaults — never break the UI just
        // because settings failed to load.
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo(() => ({ settings, loading }), [settings, loading]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => useContext(SettingsContext);

// Convenience: quick access to the global WhatsApp number as a wa.me URL.
export const useGlobalWaLink = (text) => {
  const { settings } = useSettings();
  return buildWaLink(settings.whatsapp_number, { text });
};
